import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Fire, TrendUp, Clock, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";

import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const CATEGORIES = ["All", "Models", "Funding", "Open Source", "Enterprise", "Research", "Policy", "AI News"];


function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 animate-pulse">
      <div className="h-3 bg-slate-200 rounded w-1/4 mb-3" />
      <div className="h-4 bg-slate-200 rounded w-full mb-2" />
      <div className="h-4 bg-slate-200 rounded w-3/4 mb-3" />
      <div className="h-3 bg-slate-200 rounded w-1/3" />
    </div>
  );
}

function NewsCard({ story, featured = false }) {
  const slug = story.slug || story.id;
  if (featured) {
    return (
      <Link
        to={`/trending-ai/news/${slug}`}
        className="col-span-full block rounded-2xl overflow-hidden border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group text-left"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          {story.thumbnail ? (
            <div className="relative h-[220px] md:h-[280px] bg-slate-100 overflow-hidden">
              <img
                src={story.thumbnail}
                alt={story.title}
                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                onError={(e) => { e.target.parentElement.classList.add("hidden"); }}
              />
            </div>
          ) : (
            <div className="h-[220px] md:h-[280px] bg-gradient-to-br from-slate-900 to-blue-950 flex items-center justify-center">
              <span className="text-white/10 font-black text-[80px]">AI</span>
            </div>
          )}
          <div className="p-6 bg-white flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-[10px] font-bold bg-red-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Fire size={8} weight="fill" /> FEATURED
                </span>
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{story.category}</span>
                <span className="text-xs text-slate-400">{story.publisher}</span>
              </div>
              <h2 className="text-xl font-bold text-slate-900 group-hover:text-blue-700 leading-snug mb-3 transition-colors">
                {stripHtml(story.title)}
              </h2>
              <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">{stripHtml(story.excerpt || story.summary)}</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400 mt-4">
              {story.views > 0 && <span className="flex items-center gap-1"><TrendUp size={11} /> {story.views.toLocaleString()} views</span>}
              {story.shares > 0 && <span className="flex items-center gap-1"><ArrowUpRight size={11} /> {story.shares.toLocaleString()} shares</span>}
              <span className="flex items-center gap-1"><Clock size={11} /> {story.readTime || "3 min"}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }
  return (
    <Link
      to={`/trending-ai/news/${slug}`}
      className="block bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-blue-300 hover:shadow-sm transition-all group text-left"
    >
      <div className="h-[160px] bg-slate-100 overflow-hidden">
        {story.thumbnail ? (
          <img
            src={story.thumbnail}
            alt={story.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
            onError={(e) => { e.target.style.display = "none"; e.target.nextSibling?.classList.remove("hidden"); }}
          />
        ) : null}
        <div className={`w-full h-full bg-gradient-to-br from-slate-800 to-blue-950 ${story.thumbnail ? "hidden" : ""}`} />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{story.category}</span>
          {story.hot && (
            <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <Fire size={7} weight="fill" /> HOT
            </span>
          )}
          {story.trending && (
            <span className="text-[10px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
              <TrendUp size={7} weight="fill" /> TRENDING
            </span>
          )}
        </div>
        <h3 className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 leading-snug mb-2 transition-colors">
          {stripHtml(story.title)}
        </h3>
        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2 mb-3">{stripHtml(story.excerpt || story.summary)}</p>
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>{story.publisher}{story.author && story.author !== story.publisher ? ` · ${story.author}` : ""}</span>
          <span>{story.readTime || "3 min"} read</span>
        </div>
      </div>
    </Link>
  );
}

export default function AINewsPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");

  const fetchFn = useCallback(
    (page) => fetchAISection("news", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-news",
    getCacheKey: (page) => `ai-news-p${page}`,
    ttlMs: 5 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const filtered = allItems.filter((n) => {
    const matchCat = cat === "All" || n.category === cat;
    const matchSearch = !search || n.title.toLowerCase().includes(search.toLowerCase()) || (n.excerpt || n.summary || "").toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = filtered.find((n) => n.featured);
  const rest = filtered.filter((n) => !n.featured);

  return (
    <>
      <SEO
        title="AI News — Latest Artificial Intelligence Headlines · TrendMing"
        description="Breaking AI news: model releases, funding rounds, research breakthroughs, company announcements, and the latest in artificial intelligence."
        canonical="https://trendming.com/trending-ai/news"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · News</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI News</h1>
          <p className="text-slate-500 text-sm mt-1">Breaking stories, model launches, and the latest in artificial intelligence.</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search AI news…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCat(c)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && featured && <NewsCard story={featured} featured />}
          {!loading && rest.map((story) => <NewsCard key={story.id} story={story} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No stories found</p>
              <p className="text-sm mt-1">Try adjusting your search or filters</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && (
          <div className="text-center py-4 text-xs text-red-500">{error}</div>
        )}

        {!hasMore && !loading && allItems.length > 0 && (
          <div className="text-center mt-6">
            <Link
              to="/category/technology"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-xl font-medium text-sm transition-colors"
            >
              Browse more technology news <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </div>

    </>
  );
}
